import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { IMAGES } from "../constants/site";
import { openEnquiry } from "../lib/modal";

const CARDS = [
  {
    title: "Master Layout",
    desc: "The 52-acre integrated district around Siddheshwar Lake — representative visual. Request the official layout on enquiry.",
    image: IMAGES.masterplan,
    cta: "Request Master Plan",
    testid: "floorplan-master",
  },
  {
    title: "Typical Floor Plate",
    desc: "Flexible 800 – 46,000 sq.ft carpet suites · bare-shell & warm-shell options · 4.2 m internal height.*",
    image: IMAGES.skyReflection,
    cta: "Request Unit Plans",
    testid: "floorplan-unit",
  },
];

const STACK = [
  { label: "Recreation Floor", w: "42%" },
  { label: "35 Grade A+ Office Floors", w: "58%", hot: true },
  { label: "2nd – 9th · Parking Podiums", w: "72%" },
  { label: "Ground + 1st · Destination Retail", w: "84%" },
  { label: "Lower Stilt", w: "93%" },
  { label: "Basement", w: "100%" },
];

export default function FloorPlans() {
  return (
    <section id="plans" data-testid="floorplans-section" className="py-24 md:py-32 border-t border-border">
      <div className="max-w-screen-2xl mx-auto px-6">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.35em] text-primary mb-5">Master & Unit Plans</p>
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl leading-tight mb-14">
            Planned to the <span className="italic text-primary">last square foot</span>
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.12}>
              <div data-testid={c.testid} className="group border border-border overflow-hidden">
                <motion.div
                  initial={{ clipPath: "inset(0 100% 0 0)" }}
                  whileInView={{ clipPath: "inset(0 0% 0 0)" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                  className="relative h-72 md:h-80 overflow-hidden"
                >
                  <img
                    src={c.image}
                    alt={c.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors duration-500" />
                </motion.div>
                <div className="p-8 flex items-start justify-between gap-6">
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl mb-3">{c.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
                  </div>
                  <button
                    onClick={() => openEnquiry("Request Brochure")}
                    data-testid={`${c.testid}-cta`}
                    className="shrink-0 border border-border p-3 text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                    aria-label={c.cta}
                  >
                    <ArrowUpRight size={18} />
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="text-[11px] uppercase tracking-[0.35em] text-primary mt-20 mb-3">The Stack</p>
          <h3 className="font-display font-light text-3xl md:text-4xl mb-10">
            One address — work, retail & leisure, <span className="italic text-primary">stacked</span>
          </h3>
          <div data-testid="building-stack" className="flex flex-col items-center gap-1.5">
            {STACK.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ scaleX: 0, opacity: 0 }}
                whileInView={{ scaleX: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.09, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                style={{ width: s.w }}
                data-testid={`stack-${i}`}
                className={`py-3.5 px-5 text-center text-[10px] uppercase tracking-[0.25em] border hover:brightness-95 transition-[filter] ${
                  s.hot ? "bg-primary text-primary-foreground border-primary" : "bg-white text-foreground/80 border-border"
                }`}
              >
                {s.label}
              </motion.div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
